import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ServicesSection = styled.section`
  padding: 5rem 0;
  background: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    font-family: ${props => props.theme.fonts.script};
    font-size: 3rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.textLight};
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ServiceCard = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient || props.theme.colors.gradients.primary};
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.hover};
    background: white;
  }
`;

const ServiceIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
  text-align: left;
  
  li {
    padding: 0.5rem 0;
    color: ${props => props.theme.colors.textLight};
    
    &::before {
      content: '✓';
      color: ${props => props.theme.colors.primary};
      font-weight: bold;
      margin-right: 0.5rem;
    }
  }
`;

const ServiceButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.primary};
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const CallToAction = styled(motion.div)`
  text-align: center;
  margin-top: 4rem;
  padding: 3rem 2rem;
  background: ${props => props.theme.colors.gradients.card};
  border-radius: 20px;
  
  h3 {
    font-size: 2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 2rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4);
  margin: 0 0.5rem;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(46, 204, 113, 0.6);
  }
  
  @media (max-width: 480px) {
    display: block;
    margin: 0.5rem 0;
  }
`;

const ServicesPreview = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const services = [
    {
      id: 'ponyclub',
      title: 'Ponyclub',
      icon: '🎠',
      description: 'Spielerischer Umgang mit Ponys für Kinder ab 4 Jahren. Hier lernen die Kleinen den respektvollen Umgang mit den Tieren.',
      features: [
        'Für Kinder ab 4 Jahren',
        'Spielerisches Lernen',
        'Kleine Gruppen (max. 8 Kinder)',
        'Grundlagen der Pferdepflege',
        'Erste Reiterfahrungen'
      ],
      link: '/kurse/ponyclub',
      gradient: 'linear-gradient(45deg, #2ecc71, #27ae60)'
    },
    {
      id: 'muki',
      title: 'MuKi-Club',
      icon: '👶',
      description: 'Mutter-Kind-Kurse für die ganz Kleinen ab 2 Jahren. Gemeinsame Erlebnisse mit Mama oder Papa und den Ponys.',
      features: [
        'Für Kinder ab 2 Jahren + Begleitperson',
        'Gemeinsame Erlebnisse',
        'Frühe Tierverbindung',
        'Motorik-Förderung',
        'Soziale Kompetenz'
      ],
      link: '/kurse/muki-club',
      gradient: 'linear-gradient(45deg, #ff8a80, #ffccbc)'
    },
    {
      id: 'therapy',
      title: 'Heilpädagogisches Reiten',
      icon: '🌟',
      description: 'Professionelle tiergestützte Therapie für Menschen mit besonderen Bedürfnissen. Individuelle Förderung und Entwicklung.',
      features: [
        'Individuelle Förderung',
        'Therapeutischer Ansatz',
        'Alle Altersgruppen',
        'Spezielle Bedürfnisse',
        'Entwicklungsförderung'
      ],
      link: '/kurse/heilpaedagogisches-reiten',
      gradient: 'linear-gradient(45deg, #667eea, #764ba2)'
    }
  ];

  return (
    <ServicesSection ref={ref}>
      <Container>
        <SectionHeader>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Unsere Angebote
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Von spielerischen Ponyclub-Stunden bis hin zur professionellen tiergestützten Therapie - 
            bei uns findet jeder das passende Angebot.
          </motion.p>
        </SectionHeader>

        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              gradient={service.gradient}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServiceFeatures>
                {service.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ServiceFeatures>
              <ServiceButton to={service.link}>
                Mehr erfahren
              </ServiceButton>
            </ServiceCard>
          ))}
        </ServicesGrid>

        <CallToAction
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h3>Bereit für Ihr Pony-Abenteuer?</h3>
          <p>
            Entdecken Sie die wunderbare Welt der tiergestützten Pädagogik. 
            Buchen Sie eine Schnupperstunde oder kontaktieren Sie uns für weitere Informationen.
          </p>
          <CTAButton to="/kurse">Jetzt buchen</CTAButton>
          <CTAButton to="/kurse" style={{background: 'transparent', border: '2px solid #2ecc71', color: '#2ecc71'}}>
            Alle Kurse ansehen
          </CTAButton>
        </CallToAction>
      </Container>
    </ServicesSection>
  );
};

export default ServicesPreview;